/**
 * The audit log in Arabic only (owner request 2026-10-22): every action has an Arabic name, and its details
 * show Arabic labels and values. Internal ids, technical settings and any value without an Arabic reading
 * are left out rather than shown in English.
 */
import { examKindLabel, examStatusLabel } from "../components/exams";
import {
  deletionStatusLabel,
  enrollmentStatusLabel,
  joinStatusLabel,
  personGenderLabel,
  programKindLabel,
  roleLabel,
  scopeLabel,
  userStatusLabel,
} from "./labels";
import { levelLabel } from "./permissions";
import { PERMISSION_LABEL } from "./sections";
import { POLICY_META, policyScopeLabel, strugglingIndicatorLabel, unitLabel, weekdayLabel } from "./policies";
import { volunteerStatusLabel } from "./volunteers";

export const auditActionLabel: Record<string, string> = {
  "user.create": "إنشاء حساب",
  "user.update": "تعديل بيانات حساب",
  "user.suspend": "إيقاف حساب",
  "user.reactivate": "إعادة تفعيل حساب",
  "user.password_reset": "تعيين كلمة مرور",
  "user.phone_moved": "نقل رقم جوال إلى المدير",
  "user.anonymize": "حذف بيانات حساب",
  "role.grant": "منح دور",
  "role.update": "تعديل دور",
  "role.revoke": "سحب دور",
  "role_profile.create": "إنشاء دور مخصص",
  "role_profile.update": "تعديل دور مخصص",
  "branch.create": "إنشاء مجمع",
  "branch.update": "تعديل مجمع",
  "branch.archive": "أرشفة مجمع",
  "policy.set": "تعديل سياسة",
  "policy.approve": "اعتماد سياسة",
  "policy.clear": "إزالة تخصيص سياسة",
  "program.create": "إنشاء برنامج",
  "program.update": "تعديل برنامج",
  "circle.create": "إنشاء حلقة",
  "circle.update": "تعديل حلقة",
  "circle.archive": "أرشفة حلقة",
  "circle.teacher_assign": "إسناد معلم",
  "circle.teacher_end": "إنهاء إسناد معلم",
  "enrollment.create": "تسجيل طالب",
  "enrollment.transfer": "نقل طالب",
  "enrollment.withdrawn": "انسحاب طالب",
  "enrollment.completed": "إكمال تسجيل",
  "join_request.accept": "قبول طلب انضمام",
  "join_request.reject": "رفض طلب انضمام",
  "volunteer.interview": "نقل طلب تطوع إلى المقابلة",
  "volunteer.accepted": "قبول طلب تطوع",
  "volunteer.rejected": "الاعتذار عن طلب تطوع",
  "deletion_request.submit": "طلب حذف حساب",
  "deletion_request.complete": "تنفيذ طلب حذف حساب",
  "deletion_request.reject": "رفض طلب حذف حساب",
  "organization.settings": "تعديل إعدادات الجمعية",
  "organization.init": "تهيئة الجمعية",
  "attendance.record": "تسجيل حضور",
  "attendance.update": "تصحيح حضور",
  "attendance_status.create": "إضافة حالة حضور",
  "attendance_status.update": "تعديل حالة حضور",
  "error_type.create": "إضافة نوع خطأ",
  "error_type.update": "تعديل نوع خطأ",
  "exam_type.create": "إضافة نوع اختبار",
  "exam_type.update": "تعديل نوع اختبار",
  "exam.schedule": "جدولة اختبار",
  "exam.record": "تسجيل نتيجة اختبار",
  "exam.approve": "اعتماد اختبار",
  "exam.cancel": "إلغاء اختبار",
  "holiday.create": "إضافة إجازة",
  "holiday.delete": "حذف إجازة",
  "plan.create": "إنشاء خطة",
  "plan.propose": "اقتراح خطة",
  "plan.approve": "اعتماد خطة",
  "plan.withdraw": "سحب خطة",
  "plan.resume": "استئناف خطة",
  "recitation_result.create": "تسجيل نتيجة تسميع",
  "recitation_result.correct": "تعديل نتيجة تسميع",
  "notification.announce": "إرسال إشعار",
  "report.export": "تصدير تقرير",
  "app_release.upload": "رفع نسخة تطبيق أندرويد",
  "whatsapp.settings": "تعديل إعدادات الواتساب",
  "whatsapp.connection": "ربط رقم الواتساب",
  "android_test.settings": "تعديل إعدادات اختبار أندرويد",
};

const KEY_LABEL: Record<string, string> = {
  name: "الاسم",
  title: "العنوان",
  phone: "الجوال",
  gender: "الجنس",
  role: "الدور",
  status: "الحالة",
  scope: "النطاق",
  scopeType: "النطاق",
  level: "المستوى",
  as: "بصفة",
  permissions: "الصلاحيات",
  reason: "السبب",
  note: "ملاحظة",
  city: "المدينة",
  kind: "النوع",
  date: "التاريخ",
  from: "من",
  to: "إلى",
  scheduledOn: "الموعد",
  pausedSince: "موقوفة منذ",
  studyTime: "وقت الدراسة",
  capacity: "السعة",
  archived: "مؤرشف",
  active: "مفعّل",
  enabled: "مفعّل",
  locked: "مقفل",
  countsAsPresent: "يُحسب حضورًا",
  genderSeparation: "الفصل بين الجنسين",
  dayCutoffHour: "ساعة إغلاق اليوم",
  acceptSeconds: "مهلة قبول الطلب (ثانية)",
  connectSeconds: "مهلة الاتصال (ثانية)",
  reconnectSeconds: "مهلة إعادة الاتصال (ثانية)",
  key: "السياسة",
  value: "القيمة",
  decision: "القرار",
  score: "الدرجة",
  passed: "ناجح",
  errors: "الأخطاء",
  attempt: "المحاولة",
  revision: "التعديل",
  maxScore: "الدرجة العظمى",
  passScore: "درجة النجاح",
  maxAttempts: "أقصى عدد للمحاولات",
  approverRole: "يعتمده",
  ordinal: "الترتيب",
  amount: "المقدار",
  unit: "الوحدة",
  day: "اليوم",
  periodDays: "المدة (أيام)",
  minIndicators: "أقل عدد للمؤشرات",
  repeats: strugglingIndicatorLabel.repeats,
  unfinished: strugglingIndicatorLabel.unfinished,
  absences: strugglingIndicatorLabel.absences,
  carried: strugglingIndicatorLabel.carried,
  plansMoved: "خطط نُقلت",
  provisional: "مؤقتة",
  source: "المصدر",
  format: "الصيغة",
  recipients: "المستلمون",
  channels: "طريقة الإرسال",
  sent: "وصل في التطبيق",
  whatsapp: "عبر الواتساب",
  noPhone: "بلا رقم جوال",
  versionName: "الإصدار",
  size: "الحجم (بايت)",
  tokenChanged: "تغيّر الرمز",
  demoPhone: "الحساب التجريبي",
  action: "العملية",
  state: "الحالة",
  surah: "السورة",
  ayah: "الآية",
  start: "البداية",
  end: "النهاية",
  liveTimeouts: "مهل التسميع المباشر",
  attendance: "الحضور",
};

const VALUE_LABEL: Record<string, Record<string, string>> = {
  role: { ...roleLabel, ...levelLabel },
  as: roleLabel,
  level: levelLabel,
  approverRole: roleLabel,
  gender: personGenderLabel,
  scope: { ...policyScopeLabel, ...scopeLabel },
  scopeType: scopeLabel,
  kind: { ...examKindLabel, ...programKindLabel },
  unit: unitLabel,
  day: weekdayLabel,
  decision: { passed: "ناجح", repeat: "إعادة", partial: "جزئي" },
  source: { teacher: "المعلم", session: "جلسة التسميع", auto: "تلقائي" },
  format: { xlsx: "ملف إكسل", print: "طباعة" },
  channels: { app: "التطبيق", whatsapp: "الواتساب" },
  action: { connect: "ربط", relink: "رمز جديد", logout: "قطع الاتصال" },
  state: {
    connected: "متصل",
    qr_ready: "بانتظار مسح الرمز",
    initializing: "جارٍ التجهيز",
    reconnecting: "يعيد الاتصال",
    disconnected: "غير متصل",
    not_initialized: "غير مربوط",
    unavailable: "تعذر الوصول للخدمة",
  },
  status: {
    ...enrollmentStatusLabel,
    ...userStatusLabel,
    ...joinStatusLabel,
    ...deletionStatusLabel,
    ...examStatusLabel,
    ...volunteerStatusLabel,
    draft: "مسودة",
    approved: "معتمدة",
    superseded: "مستبدلة",
    withdrawn: "مسحوبة",
    provisional: "مؤقتة",
  },
  reason: { deletion_request: "طلب حذف الحساب", account_deleted: "حذف الحساب" },
};

const ARABIC = /[؀-ۿ]/;
/** Numbers, dates, times, phone numbers and versions read the same in Arabic. */
const NEUTRAL = /^[\d\s:+\-./,]+$/;

function valueText(key: string, v: unknown, policyKey?: string): string | null {
  if (typeof v === "boolean") return v ? "نعم" : "لا";
  if (typeof v === "number") return String(v);
  if (Array.isArray(v)) {
    const parts = v.map((x) => valueText(key, x, policyKey)).filter((x): x is string => !!x);
    return parts.length ? parts.join("، ") : null;
  }
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    if (typeof o.surah === "number")
      return `السورة ${o.surah}${typeof o.ayah === "number" ? `، الآية ${o.ayah}` : ""}`;
    const inner = entries(o, policyKey);
    return inner.length ? inner.join("، ") : null;
  }
  if (typeof v !== "string" || !v) return null;
  if (key === "permissions") return PERMISSION_LABEL[v] ?? null;
  if (key === "key") return POLICY_META[v as keyof typeof POLICY_META]?.label ?? null;
  if (key === "value" && policyKey) {
    const input = POLICY_META[policyKey as keyof typeof POLICY_META]?.input;
    const option = input?.type === "select" ? input.options[v] : undefined;
    if (option) return option;
    if (v in weekdayLabel) return weekdayLabel[v as keyof typeof weekdayLabel];
  }
  const mapped = VALUE_LABEL[key]?.[v];
  if (mapped) return mapped;
  return ARABIC.test(v) || NEUTRAL.test(v) ? v : null;
}

function entries(o: Record<string, unknown>, policyKey?: string): string[] {
  const pk = typeof o.key === "string" ? o.key : policyKey;
  return Object.entries(o).flatMap(([k, v]) => {
    const label = KEY_LABEL[k];
    if (!label || v === null || v === undefined || v === "") return [];
    const text = valueText(k, v, pk);
    return text ? [`${label}: ${text}`] : [];
  });
}

/** An audit payload as Arabic «label: value» parts; nothing in English. */
export function auditDetails(value: unknown): string {
  if (!value || typeof value !== "object") return "—";
  return entries(value as Record<string, unknown>).join(" · ") || "—";
}
